import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Button,
    Chip,
    FormControl,
    MenuItem,
    Select,
    Typography,
} from '@material-ui/core'
import {
    MailOutline as OnboardIcon,
    ExitToApp as OffboardIcon,
    Assignment as SurveyIcon,
    NotificationsOutlined as ReminderIcon,
    HelpOutline as OtherIcon,
    GetApp as DownloadIcon,
    Refresh as RefreshIcon,
} from '@material-ui/icons'

import { BackdropProgress } from '../../components'
import { send_command } from '../../components/api'
import { decodeEmailTemplateBody } from 'utils/decodeEmailTemplateBody'
import { formatUtcTimestamp } from 'utils/formatUtcTimestamp'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'

const TYPE_ICONS = {
    onboarding: { icon: OnboardIcon, bg: '#e0f5ef' },
    offboarding: { icon: OffboardIcon, bg: '#ffe8e8' },
    survey: { icon: SurveyIcon, bg: '#fff3dc' },
    reminder: { icon: ReminderIcon, bg: '#ede8ff' },
}

function getTypeConfig(name) {
    const key = (name || '').toLowerCase()
    for (const [k, v] of Object.entries(TYPE_ICONS)) {
        if (key.includes(k)) return v
    }
    return { icon: OtherIcon, bg: '#eef0f4' }
}

function renderEmailBody(body) {
    try {
        return body.replaceAll(
            '<%= it.tenant_img %>',
            'https://s3.us-west-2.amazonaws.com/cdn.dataprotected.com/Logo.png'
        )
    } catch {
        return body
    }
}

function latestRetrievedAt(emails) {
    if (!emails?.length) return null
    return emails.reduce((latest, email) => {
        const ts = email.retrieved_at
        if (!ts) return latest
        if (!latest || ts > latest) return ts
        return latest
    }, null)
}

function emailKey(email) {
    return `${email.name}-${email.language}`
}

function isSameEmail(a, b) {
    return a && b && a.name === b.name && a.language === b.language
}

export const ClientEmails = ({ tenant_id, refreshToken = 0 }) => {
    const [isLoading, setIsLoading] = useState(false)
    const [emails, setEmails] = useState([])
    const [fetchError, setFetchError] = useState(null)
    const [filterType, setFilterType] = useState('')
    const [filterLang, setFilterLang] = useState('')
    const [selectedEmail, setSelectedEmail] = useState(null)
    const [lastRetrieved, setLastRetrieved] = useState(null)

    const fetchEmails = useCallback(() => {
        if (!tenant_id) return
        setIsLoading(true)
        setFetchError(null)
        send_command('ADMIN_GET_CLIENT_EMAILS', { tenant_id })
            .then(data => {
                const list = Array.isArray(data) ? data : []
                setEmails(list)
                setLastRetrieved(latestRetrievedAt(list))
                setSelectedEmail(null)
            })
            .catch(ex => {
                setFetchError(getApiErrorMessage(ex, 'Failed to load client emails'))
            })
            .finally(() => setIsLoading(false))
    }, [tenant_id])

    useEffect(() => {
        fetchEmails()
    }, [fetchEmails])

    useEffect(() => {
        if (refreshToken > 0) fetchEmails()
    }, [refreshToken, fetchEmails])

    const emailTypes = useMemo(
        () => [...new Set(emails.map(e => e.name).filter(Boolean))].sort(),
        [emails]
    )
    const emailLangs = useMemo(
        () => [...new Set(emails.map(e => e.language).filter(Boolean))].sort(),
        [emails]
    )

    const filtered = useMemo(() => emails.filter(e => {
        const typeMatch = !filterType || e.name === filterType
        const langMatch = !filterLang || e.language === filterLang
        return typeMatch && langMatch
    }), [emails, filterType, filterLang])

    useEffect(() => {
        if (filtered.length === 0) {
            setSelectedEmail(null)
            return
        }
        setSelectedEmail(prev => (
            prev && filtered.some(e => isSameEmail(e, prev)) ? prev : filtered[0]
        ))
    }, [filtered])

    const downloadAll = () => {
        if (!emails.length) return
        const blob = new Blob([JSON.stringify(emails, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.download = 'client_emails.json'
        link.href = url
        link.click()
        URL.revokeObjectURL(url)
    }

    if (isLoading && emails.length === 0) {
        return <BackdropProgress />
    }

    return (
        <div className="superadmin-client-emails">
            <div className="superadmin-client-emails__meta">
                <span className="superadmin-client-emails__meta-label">Last retrieved</span>
                <span className="superadmin-client-emails__meta-value">
                    {lastRetrieved ? formatUtcTimestamp(lastRetrieved) : '—'}
                </span>
            </div>

            {fetchError && (
                <div className="superadmin-client-emails__error">
                    <Typography color="error">{fetchError}</Typography>
                    <Button size="small" variant="outlined" onClick={fetchEmails}>
                        Retry
                    </Button>
                </div>
            )}

            {!fetchError && (
                <>
                    <div className="superadmin-client-emails__filters">
                        <FormControl variant="outlined" size="small" className="superadmin-client-emails__filter">
                            <label className="superadmin-client-emails__filter-label" htmlFor="client-email-type-filter">
                                Type
                            </label>
                            <Select
                                id="client-email-type-filter"
                                value={filterType}
                                onChange={e => setFilterType(e.target.value)}
                                displayEmpty
                                renderValue={v => v || 'All Types'}
                            >
                                <MenuItem value="">All Types</MenuItem>
                                {emailTypes.map(t => (
                                    <MenuItem key={t} value={t}>{t}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl variant="outlined" size="small" className="superadmin-client-emails__filter superadmin-client-emails__filter--wide">
                            <label className="superadmin-client-emails__filter-label" htmlFor="client-email-lang-filter">
                                Language
                            </label>
                            <Select
                                id="client-email-lang-filter"
                                value={filterLang}
                                onChange={e => setFilterLang(e.target.value)}
                                displayEmpty
                                renderValue={v => v || 'All Languages'}
                            >
                                <MenuItem value="">All Languages</MenuItem>
                                {emailLangs.map(l => (
                                    <MenuItem key={l} value={l}>{l}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={downloadAll}
                            disabled={!emails.length}
                        >
                            Download all
                        </Button>

                        <Button
                            className="superadmin-client-emails__refresh"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={fetchEmails}
                            disabled={isLoading}
                        >
                            Refresh
                        </Button>
                    </div>

                    {filtered.length === 0 ? (
                        <Typography className="superadmin-client-emails__empty">
                            {emails.length === 0
                                ? 'No client emails retrieved yet — use Fetch Emails in Remote Control.'
                                : 'No templates match the selected filters.'}
                        </Typography>
                    ) : (
                        <div className="superadmin-client-emails__split">
                            <div className="superadmin-client-emails__list">
                                {filtered.map(email => {
                                    const cfg = getTypeConfig(email.name)
                                    const Icon = cfg.icon
                                    const isSelected = isSameEmail(selectedEmail, email)
                                    return (
                                        <button
                                            key={emailKey(email)}
                                            type="button"
                                            className={[
                                                'superadmin-client-emails__row',
                                                isSelected ? 'is-selected' : '',
                                            ].filter(Boolean).join(' ')}
                                            onClick={() => setSelectedEmail(email)}
                                        >
                                            <div
                                                className="superadmin-client-emails__type-icon"
                                                style={{ backgroundColor: cfg.bg }}
                                            >
                                                <Icon fontSize="small" style={{ color: '#555' }} />
                                            </div>
                                            <div className="superadmin-client-emails__info">
                                                <div className="superadmin-client-emails__title">
                                                    {email.subject || email.name}
                                                </div>
                                                <div className="superadmin-client-emails__subtitle">
                                                    {email.name}
                                                </div>
                                            </div>
                                            <Chip
                                                label={email.language}
                                                size="small"
                                                className="superadmin-client-emails__lang"
                                                variant="outlined"
                                            />
                                        </button>
                                    )
                                })}
                            </div>

                            <div className="superadmin-client-emails__preview">
                                {selectedEmail ? (
                                    <>
                                        <div className="superadmin-client-emails__preview-caption">
                                            {selectedEmail.name} — {selectedEmail.language}
                                            {selectedEmail.subject ? ` · ${selectedEmail.subject}` : ''}
                                        </div>
                                        <div
                                            className="superadmin-client-emails__preview-sheet"
                                            dangerouslySetInnerHTML={{
                                                __html: renderEmailBody(
                                                    decodeEmailTemplateBody(selectedEmail.body || '')
                                                ) || '<p style="color:#9aa3b2">No preview available for this template.</p>',
                                            }}
                                        />
                                    </>
                                ) : (
                                    <div className="superadmin-client-emails__preview-empty">
                                        Select an email to preview its content.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
